import { useState } from "react";
import { FaCaretDown } from "react-icons/fa";
import { ChallengeInfoResponse, ChallengeFaqDetails } from "@devour/client";
import DOMPurify from "dompurify";

// Extended challenge type for faqLeftContent field
interface ExtendedChallenge extends ChallengeInfoResponse {
    faqLeftContent?: string;
}

interface ChallengeFAQProps {
    challenge: ChallengeInfoResponse;
}

/**
 * FAQ section component for challenge pages.
 * Combines dynamic FAQ questions from challenge.faqs with configurable left-side content.
 *
 * The left side content can be configured via challenge.faqLeftContent (HTML string)
 * in the admin Page Design tab. This allows customizing:
 * - "Got Questions?" header
 * - "Frequently Asked Questions" subtitle
 * - Official Giveaway Rules
 * - Sponsor disclaimer text
 *
 * If no faqLeftContent is configured, sensible defaults are shown.
 */
export const ChallengeFAQ = ({ challenge }: ChallengeFAQProps) => {
    const extendedChallenge = challenge as ExtendedChallenge;
    const faqs = challenge?.faqs;
    const faqLeftContent = extendedChallenge?.faqLeftContent;

    // Don't render if no FAQs
    if (!faqs || faqs.length === 0) {
        return null;
    }

    return (
        <div className="challenge-page_faq">
            <div className="challenge-page_faq_left">
                {faqLeftContent
                    ? <div
                        className="challenge-page_faq_left_custom-content"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(faqLeftContent) }}
                    />
                    : <>
                        <div className="challenge-page_faq_left_got-questions">
                            GOT QUESTIONS?
                        </div>
                        <div className="challenge-page_faq_left_faqs">
                            Frequently Asked Questions (FAQs)
                        </div>
                        {challenge.termsUrl &&
                            <div className="challenge-page_faq_left_giveaway-rules">
                                <div className="challenge-page_faq_left_giveaway-rules_title">
                                    Official Giveaway Rules
                                </div>
                                <div className="challenge-page_faq_left_giveaway-rules_description">
                                    <a href={challenge.termsUrl} target="_blank" rel="noopener noreferrer">
                                        View Official Rules
                                    </a>
                                </div>
                            </div>
                        }
                    </>
                }
            </div>
            <div className="challenge-page_faq_right">
                {faqs.map((faq, index) =>
                    <FaqItem key={index} {...faq} />)}
            </div>
        </div>
    );
};

/**
 * Individual FAQ accordion item
 */
const FaqItem = (props: ChallengeFaqDetails) => {
    const { question, answer } = props;
    const [isOpen, setIsOpen] = useState(false);

    const toggleOpen = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div className="challenge-page_faq-item">
            <div className="challenge-page_faq-item_question" onClick={toggleOpen}>
                {question}
                <FaCaretDown
                    className={`challenge-page_faq-item_arrow-icon ${isOpen ? "open" : ""}`}
                    size={16}
                    color="#FFFFFF"
                />
            </div>
            {isOpen &&
                <div className="challenge-page_faq-item_answer">
                    {answer}
                </div>
            }
        </div>
    );
};

export default ChallengeFAQ;
